import { useTheme } from '@emotion/react'
import { Box, Button, Divider, Typography, CircularProgress, Checkbox, FormControlLabel } from '@mui/material'
import { IconHelpCircleFilled } from '@tabler/icons-react'
import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { Dialog } from '~/components/Dialog'
import { useContract } from '~/contexts'
import { useContractChangeFlagCanCreateInvoicesMutation } from '~/graphql/types'

export type ContractCanCreateInvoicesDialogProps = {
  isVisible: boolean
  toggleModal: () => void
}

export const ContractCanCreateInvoicesDialog: React.FC<ContractCanCreateInvoicesDialogProps> = ({ isVisible, toggleModal }) => {
  const theme = useTheme()
  const { contract, setContract } = useContract()
  const [isChecked, setIsChecked] = useState(false)

  useEffect(() => {
    if (isVisible) {
      setIsChecked(false)
    }
  }, [isVisible])

  const [updateCanCreateInvoices, { loading }] = useContractChangeFlagCanCreateInvoicesMutation({
    onCompleted(updatedContract) {
      if (contract) {
        setContract({ ...contract, canCreateInvoices: updatedContract.contractChangeFlagCanCreateInvoices.canCreateInvoices })
        toast.success('Permissão de criar faturas atualizada com sucesso')
        toggleModal()
        setIsChecked(false)
      }
    },
    onError() {
      toast.error('Erro ao atualizar permissão de criar faturas.')
    },
  })

  const handleConfirm = () => {
    if (isChecked) {
      const newValue = !contract?.canCreateInvoices
      updateCanCreateInvoices({
        variables: {
          id: contract?._id || '',
          canCreate: newValue
        }
      })
    }
  }

  const currentValue = contract?.canCreateInvoices

  return (
    <Dialog
      isVisible={isVisible}
      title='Alterar permissão do contrato'
      onClose={toggleModal}
      minWidth={700}
      titleIcon={<IconHelpCircleFilled style={{ color: theme.palette.primary.main }} size={32} />}
    >
      <Divider />
      <Box sx={{ display: 'flex', flexFlow: 'column', gap: '1.5rem', padding: '2rem 0' }}>
        <Typography variant='body1'>
          {currentValue 
            ? <>Atualmente é <b>PERMITIDO</b> gerar novas faturas para esse contrato em desativação, gostaria de alterar esse status para:</> 
            : <>Atualmente é <b>NÃO PERMITIDO</b> gerar novas faturas para esse contrato em desativação, gostaria de alterar esse status para:</>
          }
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              sx={{ paddingLeft: '0.5rem' }}
            />
          }
          label={<Typography variant='body1'>{currentValue ? <><b>NÃO PERMITIR</b> gerar novas faturas</> : <><b>PERMITIR</b> gerar novas faturas</>}</Typography>}
        />
      </Box>
      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '2rem' }}>
        <Button disabled={loading} onClick={toggleModal} color='secondary'>Cancelar</Button>
        <Button
          endIcon={loading ? <CircularProgress size={24} color='inherit' /> : null}
          disabled={!isChecked || loading}
          onClick={handleConfirm}
        >
          Confirmar
        </Button>
      </Box>
    </Dialog>
  )
}
